/**
 * Hover tooltip for truncated text. Renders content with `truncate`;
 * if the rendered span overflows its container, hovering reveals a
 * fixed-positioned overlay with the full text wrapped to multiple
 * lines. No tooltip if content fits — keeps the UI quiet for short
 * values.
 *
 * Native `title` attributes work but have ~1s delay, single-line
 * wrapping, and platform-dependent styling. This pattern lets the
 * tooltip appear immediately, match the rest of the dashboard's
 * popover treatment, and handle long multi-value lists (installed
 * AI agents, SHA256s, etc.) cleanly.
 */
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TOOLTIP_LAYER } from "../lib/tooltipLayer.js";

const TOOLTIP_MAX_WIDTH = 420;

export default function OverflowTooltip({
    text,
    children,
    // Optional rich content for the tooltip body. Falls back to
    // `text` when not supplied. Pass JSX here when the cell renders
    // mixed-color spans (e.g. agent name in ink + version in muted)
    // so the tooltip carries the same visual treatment.
    tooltipContent,
    className = "",
    // Force the tooltip on every hover, not just when the text overflows.
    // Used for cells whose visible text is a lossy abbreviation (e.g. a
    // compacted "8.8M") so the exact value stays reachable even though the
    // short form fits without overflowing.
    always = false,
    // How many lines the trigger may use before truncating. 1 (default) →
    // single-line ellipsis. >1 → wrap at WORD boundaries up to N lines,
    // then clamp with an ellipsis — never breaks mid-word. The tooltip
    // fires on horizontal overflow (a single word too wide to fit) or
    // vertical overflow (content beyond N lines).
    lines = 1,
    as: Tag = "span",
    // Keep the DOM forwarding surface explicit. This component is part of
    // the render path, so an opaque prop bag could smuggle href/src or a
    // URL-capable style around the standing sink check.
    type,
    disabled,
    onClick,
    textColor,
    // This component renders an interactive element when `as="button"`, so it
    // has to be able to carry that element's ARIA. Named rather than a `...rest`
    // spread: an open spread would let any caller leak arbitrary DOM props onto
    // a shared primitive.
    ariaLabel,
}) {
    const ref = useRef(null);
    const tipRef = useRef(null);
    const correctedRef = useRef(false);
    const [tooltip, setTooltip] = useState(null); // { top, left } | null

    const onEnter = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        // Only show when the trigger actually overflows (unless `always`).
        // Horizontal: a too-long single word in clamp mode, or any
        // ellipsised single line. Vertical: content beyond the clamp.
        const overflowing = el.scrollWidth > el.clientWidth || el.scrollHeight > el.clientHeight;
        if (!always && !overflowing) return;
        const r = el.getBoundingClientRect();
        // Anchor the tooltip's left edge under the trigger; the layout
        // effect below re-clamps it into the viewport once its REAL width
        // is known. (The prior MAX_WIDTH-based pre-clamp shoved short
        // tooltips far to the left whenever the trigger sat near the right
        // edge — e.g. a "Coverage" label on the right-most KPI tile.)
        correctedRef.current = false;
        setTooltip({ top: r.bottom + 4, left: r.left });
    }, [always]);

    const onLeave = useCallback(() => {
        correctedRef.current = false;
        setTooltip(null);
    }, []);

    // Once the tooltip is on screen, measure it and correct only what actually
    // overflows: nudge it left so it never runs off the right edge, and flip it
    // to whichever side of the trigger has more room when it would run off the
    // bottom (the case a long installed-agents list hits on a row near the
    // bottom of the table). Same edge-correction HoverTooltip performs, capping
    // height to the available space rather than letting the viewport clip it.
    // Runs at most once per show, so a tooltip that already fits is untouched.
    useLayoutEffect(() => {
        if (!tooltip || correctedRef.current) return;
        const tipEl = tipRef.current;
        const trigEl = ref.current;
        if (!tipEl || !trigEl) return;
        correctedRef.current = true;
        const tip = tipEl.getBoundingClientRect();
        const trig = trigEl.getBoundingClientRect();
        const M = 8;
        const fix = {};

        const left = Math.max(M, Math.min(tooltip.left, window.innerWidth - tip.width - M));
        if (Math.abs(left - tooltip.left) > 0.5) fix.left = left;

        if (tip.top < M || tip.bottom > window.innerHeight - M) {
            const roomAbove = trig.top - M;
            const roomBelow = window.innerHeight - trig.bottom - M;
            if (roomBelow >= roomAbove) {
                fix.top = trig.bottom + 4;
                fix.transform = "none";
                fix.maxHeight = Math.max(80, Math.floor(roomBelow));
            } else {
                fix.top = trig.top - 6;
                fix.transform = "translateY(-100%)";
                fix.maxHeight = Math.max(80, Math.floor(roomAbove));
            }
        }

        if (Object.keys(fix).length) setTooltip((t) => (t ? { ...t, ...fix } : t));
    }, [tooltip]);

    const content = children ?? text;
    // `truncate` (nowrap + ellipsis) for one line; `line-clamp-N` (wrap at
    // word boundaries, ellipsis after N lines) for multiline. Literal class
    // names so Tailwind's JIT keeps them.
    const clampClass = lines >= 3 ? "line-clamp-3" : lines === 2 ? "line-clamp-2" : "truncate";

    return (
        <>
            <Tag
                ref={ref}
                type={type}
                disabled={disabled}
                onClick={onClick}
                aria-label={ariaLabel}
                onMouseEnter={onEnter}
                onMouseLeave={onLeave}
                className={[clampClass, className].join(" ").trim()}
                style={{ color: textColor }}
            >
                {content}
            </Tag>
            {tooltip ? createPortal(
                <div
                    ref={tipRef}
                    role="tooltip"
                    className={`pointer-events-none fixed ${TOOLTIP_LAYER} whitespace-normal break-words border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[8px] py-[6px] text-[11px] leading-[1.3] text-[color:var(--ink)] shadow-lg`}
                    style={{
                        top: `${tooltip.top}px`,
                        left: `${tooltip.left}px`,
                        maxWidth: `${TOOLTIP_MAX_WIDTH}px`,
                        ...(tooltip.transform ? { transform: tooltip.transform } : null),
                        ...(tooltip.maxHeight != null
                            ? { maxHeight: tooltip.maxHeight, overflow: "hidden" }
                            : null),
                    }}
                >
                    {tooltipContent ?? text}
                </div>,
                document.body,
            ) : null}
        </>
    );
}
